import React from "react";
import Card from "components/Cards/Card";
import npsIcon from "assets/Icon_Tab_09.png";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  XAxis,
} from "recharts";
import LoadingIndicator from "../../ui/LoadingIndicator";

type NpsDistributionPoint = {
  score: number;
  count: number;
};

type NpsChartData = {
  nps: number | null;
  totalResponses: number;
  distribution: NpsDistributionPoint[];
};

type NpsChartProps = {
  href: string | null;
  data: NpsChartData;
  customName?: string;
  isLoading?: boolean;
};

// Per the client's mockup (HTML supplied in the brief):
const DETRACTOR_COLOUR = "#F0997B";
const PASSIVE_COLOUR = "#FAC775";
const PROMOTER_COLOUR = "#97C459";

function categoryColour(score: number): string {
  if (score <= 6) return DETRACTOR_COLOUR;
  if (score <= 8) return PASSIVE_COLOUR;
  return PROMOTER_COLOUR;
}

const NpsChart: React.FC<NpsChartProps> = ({
  href,
  data,
  customName,
  isLoading = true,
}) => {
  if (isLoading) {
    return (
      <Card href={href} icon={npsIcon} title={customName ?? "NPS"}>
        <div
          className="CardBody"
          style={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingIndicator />
        </div>
      </Card>
    );
  }

  const hasData =
    data &&
    data.nps !== null &&
    data.totalResponses > 0 &&
    data.distribution?.length === 11;

  if (!hasData) {
    return (
      <Card href={href} icon={npsIcon} title={customName ?? "NPS"}>
        <div className="CardBody">
          <div className="NoChartData">
            <p>No results found</p>
          </div>
        </div>
      </Card>
    );
  }

  const total = data.totalResponses;
  const detractors = data.distribution
    .filter(p => p.score <= 6)
    .reduce((s, p) => s + p.count, 0);
  const passives = data.distribution
    .filter(p => p.score >= 7 && p.score <= 8)
    .reduce((s, p) => s + p.count, 0);
  const promoters = data.distribution
    .filter(p => p.score >= 9)
    .reduce((s, p) => s + p.count, 0);

  const pct = (n: number) => (n / total) * 100;
  const dPct = pct(detractors);
  const paPct = pct(passives);
  const prPct = pct(promoters);

  return (
    <Card href={href} icon={npsIcon} title={customName ?? "NPS"}>
      <div className="CardBody NpsChartBody">
        <div className="NpsChartScorePanel">
          <div className="NpsChartScoreBlock">
            <p className="NpsLabel">NET PROMOTER SCORE</p>
            <p className="NpsScoreValue">{data.nps!.toFixed(1)}</p>
          </div>
          <div className="NpsChartTotalBlock">
            <p className="NpsLabel">TOTAL RESPONSES</p>
            <p className="NpsTotalValue">{total.toLocaleString("en-GB")}</p>
          </div>
        </div>
        <div className="NpsChartDistribution">
          <p className="NpsLabel">RESPONSE DISTRIBUTION</p>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={data.distribution}
              margin={{ top: 20, bottom: 4, left: 8, right: 8 }}
            >
              <XAxis
                dataKey="score"
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <Bar
                dataKey="count"
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
                maxBarSize={36}
              >
                {data.distribution.map(point => (
                  <Cell
                    key={point.score}
                    fill={categoryColour(point.score)}
                  />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  fill="#3a4550"
                  fontSize={11}
                  fontWeight={500}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div
            className="NpsCategoryBand"
            style={{
              gridTemplateColumns: `${Math.max(dPct, 0.001)}fr ${Math.max(
                paPct,
                0.001
              )}fr ${Math.max(prPct, 0.001)}fr`,
            }}
          >
            <div
              className="NpsCategorySegment"
              style={{ backgroundColor: DETRACTOR_COLOUR }}
              title="Detractors: scores 0 to 6"
            >
              <span>{dPct.toFixed(1)}%</span>
            </div>
            <div
              className="NpsCategorySegment"
              style={{ backgroundColor: PASSIVE_COLOUR }}
              title="Passives: scores 7 to 8"
            >
              <span>{paPct.toFixed(1)}%</span>
            </div>
            <div
              className="NpsCategorySegment"
              style={{ backgroundColor: PROMOTER_COLOUR }}
              title="Promoters: scores 9 to 10"
            >
              <span>{prPct.toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default NpsChart;
